import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../common/Button';
import icons from '../../../../constants/icons';
import type { SelectableGame } from './steamGameImportTypes';

export interface SteamGameImportSelectionViewProps {
  games: SelectableGame[];
  onToggleGame: (appId: string) => void;
  onTogglePackage: (appId: string, packageId: string) => void;
  onBack: () => void;
  onStartImport: () => void;
}

function SteamGameImportSelectionView({
  games,
  onToggleGame,
  onTogglePackage,
  onBack,
  onStartImport,
}: SteamGameImportSelectionViewProps) {
  const { t } = useTranslation();

  const selectedGames = games.filter((g) => g.selected).length;
  const selectedSkus = games.reduce(
    (count, game) => count + game.packages.filter((p) => p.selected).length,
    0
  );
  const existingGameCount = games.filter((g) => g.existsInAlaska).length;
  const existingSkuCount = games.reduce(
    (count, game) => count + game.packages.filter((p) => p.existsInAlaska).length,
    0
  );
  const canImportSelection = selectedSkus > 0;

  return (
    <div className="row justify-content-center">
      <div className="col-xl-10">
        <div className="card glass-panel p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">{t('Review Games and SKUs to Import')}</h4>
            <span className="badge bg-dark border border-secondary text-light">
              {t('{{games}} games / {{skus}} SKUs selected', {
                games: selectedGames,
                skus: selectedSkus,
              })}
            </span>
          </div>

          <div
            className="border border-secondary-subtle rounded p-3 mb-3 overflow-y-auto"
            style={{ maxHeight: '420px' }}
          >
            {games.map((game) => (
              <div key={game.appId} className="mb-3 pb-3 border-bottom border-secondary-subtle">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={game.selected}
                    onChange={() => onToggleGame(game.appId)}
                  />
                  <strong>{game.name}</strong>
                  <code className="text-blue">{game.appId}</code>
                  {game.existsInAlaska && (
                    <span className="badge bg-warning-subtle text-warning">
                      {t('Already in Alaska')}
                    </span>
                  )}
                </div>

                <div className="ms-4">
                  {game.packages.length === 0 ? (
                    <div className="text-secondary small">{t('No Steam packages found')}</div>
                  ) : (
                    game.packages.map((pkg) => (
                      <label key={pkg.packageId} className="d-flex align-items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          checked={pkg.selected}
                          onChange={() => onTogglePackage(game.appId, pkg.packageId)}
                        />
                        <span>{pkg.name}</span>
                        <code className="text-blue">{pkg.packageId}</code>
                        {pkg.existsInAlaska && (
                          <span className="badge bg-warning-subtle text-warning">
                            {t('SKU exists')}
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="small text-light opacity-75 mb-3">
            {t(
              'Items marked "Already in Alaska" or "SKU exists" are skipped and will not be inserted again.'
            )}{' '}
            {existingGameCount > 0 || existingSkuCount > 0
              ? t('Detected: {{games}} existing game(s), {{skus}} existing SKU(s).', {
                  games: existingGameCount,
                  skus: existingSkuCount,
                })
              : ''}
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <Button
              className="btn btn-secondary"
              onClick={onBack}
              icon={icons.CHEVRON_LEFT}
              text={t('Back')}
              title={t('Back')}
            />
            <Button
              className="btn btn-primary"
              onClick={onStartImport}
              icon={icons.BOLT}
              text={t('Import Selected')}
              title={
                canImportSelection
                  ? t('Import Selected')
                  : t('No new SKUs selected to import')
              }
              isDisabled={!canImportSelection}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SteamGameImportSelectionView;
